import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Fragment
} from "react";
import Popover from "../popover";
import Tooltip from "../tooltip";
import ChevronDown from "../../icons/chevronDownIcon";
import CloseIcon from "../../icons/closeIcon";
import SearchIcon from "../../icons/searchIcon";
import styles from "./style.module.css";

const SearchDropdown = (props) => {
  const { value, schema, onChange, label, id, required, options } = props;
  const newOptions = schema.options;
  const {
    tooltipText,
    position,
    transform,
    customClass,
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(newOptions);
  const [selectedOption, setSelectedOption] = useState(
    value ? JSON.parse(value) : null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setSearchQuery("");
    setFilteredOptions(newOptions);
  };

  const handleOptionClick = (item) => {
    setSelectedOption(item);
    setIsOpen(false);
    onChange(JSON.stringify(item));
  };

  const handleClear = () => {
    onChange(JSON.stringify({
      value: '',
      title: '',
    }));
    setSelectedOption({
      value: '',
      title: '',
    });
  };

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      const filtered = newOptions.filter((item) =>
        item.title.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredOptions(filtered);
    },
    [newOptions]
  );

  const debouncedSearch = useMemo(() => {
    const delay = 500;
    let timeoutId;
    return (value) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => handleSearch(value), delay);
    };
  }, [handleSearch]);

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      let temp = JSON.parse(value);
      const filtered = newOptions.filter((item) =>
        item.value === temp.value
      );
      setSelectedOption(filtered[0]);
    }
  }, [newOptions, value]);

  return (
    <Fragment>
      {label && (
        <label className="control-label" htmlFor={id}>
          <b>{label}</b>
          {required && <span className="required">*</span>}
          {tooltipText && (
            <Tooltip
              content={tooltipText}
              position={position}
              transform={transform}
              customClass={`${customClass} ${styles.tooltip_icon}`}
            />
          )}
        </label>
      )}
      <div
        className={`${styles.search_dropdown} ${schema.customClass}`}
        ref={dropdownRef}
      >
        <button
          ref={buttonRef}
          type="button"
          className={styles.search_dropdown_btn}
          onClick={toggleDropdown}
          tabIndex={0}
        >
          <span className={styles.search_dropdown_btn_text}>
            {selectedOption?.title || schema.placeholder}
          </span>
          {(!schema.clearable || !selectedOption?.title) && <ChevronDown className={styles.chevron_down} />}
        </button>
        {schema.clearable && selectedOption?.title && <span className={styles.close_icon} onClick={handleClear}><CloseIcon /></span>}
        <Popover open={isOpen} buttonRef={buttonRef} onClose={toggleDropdown} >
          <div className={styles.search_dropdown_options}>
            <div className={styles.sdo_search_input}>
              <input
                type="text"
                placeholder="Search"
                ref={searchInputRef}
                tabIndex={0}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  debouncedSearch(e.target.value);
                }}
              />
              <SearchIcon className={styles.search_icon} />
            </div>
            <div className={styles.sdo_options}>
              {filteredOptions.map((item, index) => (
                <div
                  className={`${styles.sdo_option} ${selectedOption?.value === item.value
                    ? styles.sdo_selected
                    : ""
                    }`}
                  key={index}
                  tabIndex={0}
                  onClick={() => handleOptionClick(item)}
                >
                  {item.title}
                </div>
              ))}
            </div>
          </div>
        </Popover>
      </div>
    </Fragment>
  );
};

export default SearchDropdown;
